import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import {GlobalCurrency} from '../../GlobalCurrency/GlobalCurrency';
import BudgetLineItemsTable from './BudgetLineItemsTable';
import ArtistPaymentRows from './ArtistPaymentRows';
import TagManagerApp from '../../TagManager/startup/TagManagerApp';
import SettlementVenueNetTable from '../../../shared/SettlementVenueNetTable';
import useSettlementOfferAutosave from '../../../shared/useSettlementOfferAutosave';

import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
var _ = require('lodash');

var formatter;
const formatCurrency = function(value){
  var abs = Math.abs(value);

  if(value < 0){
    return ("(" + formatter.format(abs) + ")");
  } else {
    return formatter.format(abs);
  }
}

function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function offerDates(offer, confirm, team) {
  const showDates = team?.show_hold_dates_on_offer ?? true;
  const showPositions = team?.show_hold_positions_on_offer ?? false;

  if (confirm && confirm.type === "Confirm" && confirm.multi_day) {
    return(
      <p style={{ "margin": "0px 0px 2px 0px", "fontWeight": "bold" }}>
        {confirm.human_date_and_time_with_day_of_week}
      </p>
    )
  } else if (confirm && confirm.type === "Confirm") {
    let holdPositionDate = new Date(confirm.hold_positions[0].start_time);

    const dateContent = holdPositionDate.toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: offer.venue_ownership.tzinfo_identifier
    });

    return (
      <p style={{ "margin": "0px 0px 2px 0px", "fontWeight": "bold" }}>
        {dateContent}
      </p>
    );
  } else if (confirm && confirm.type === "Hold") {
    const holdPositions = confirm.hold_positions
      .map(hold_position => ({
        ...hold_position,
        date: hold_position.start_time ? new Date(hold_position.start_time) : null
      }))
      .filter(hold => hold.date instanceof Date && !isNaN(hold.date))
      .sort((a, b) => a.date - b.date);

    if (!showDates && !showPositions) {
      return (
        <p style={{ "margin": "0px 0px 2px 0px", "fontWeight": "bold" }}>
          Mutually Agreeable Date
        </p>
      );
    }

    const holdLabel = holdPositions.map((hold, i) => {
      const position = showPositions ? `H${hold.position || i + 1}` : null;
      const date = showDates ? hold.date.toLocaleDateString("en-US", {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: offer.venue_ownership.tzinfo_identifier
      }) : null;

      if (position && date) return `${position}: ${date}`;
      if (position) return position;
      return date;
    }).join(', ');

    return holdLabel ? (
      <p style={{ "margin": "0px 0px 2px 0px" }}>
        <span style={{ "fontWeight": "bold" }}>Mutually Agreeable Date</span>
        {` - Holds: ${holdLabel}`}
      </p>
    ) : null;
  } else if (offer.human_start_date) {
    return (
      <p style={{ "margin": "0px 0px 2px 0px", "fontWeight": "bold" }}>
        {offer.human_start_date}
      </p>
    );
  } else {
    return (
      <p style={{ "margin": "0px 0px 2px 0px", "fontWeight": "bold" }}>
        Mutually Agreeable Date
      </p>
    );
  }
}

function offerLocation(offer) {
  let venueName;
  let ageRestriction = "";
  let address = offer.venue.address_from_components;

  if (offer.rooms_subheader) {
    venueName = `${offer.venue.name} (${offer.rooms_subheader})`;
  } else {
    venueName = offer.venue.name;
  }

  if(offer.age_restriction) {
    ageRestriction = ` (${offer.age_restriction})`
  }

  if (address) {
    return `${venueName}${ageRestriction} - ${address}`;
  } else {
    return `${venueName}${ageRestriction}`;
  }
}

function artistPercentageDescription(budget, calculated) {
  var andOrVs;

  if(budget.dollar_or_percentage_operator === "AND"){
    andOrVs = "and";
  } else {
    andOrVs = "or";
  };

  return `${andOrVs} ${calculated.showSummary.upsidePercentage}% of Calculated Backend`;
}

function parsedUpsidePercentage(budget) {
  var upsidePercentage;

  if(budget.upside_percentage){
    upsidePercentage = parseFloat(budget.upside_percentage);
  } else {
    upsidePercentage = 0.00;
  }

  return upsidePercentage;
}

function hasUpsidePercentage(budget) {
  return parsedUpsidePercentage(budget) > 0.00;
}

function dealLanguage(offer, hasUpside) {
  if (!hasUpside) return " flat guarantee";
  if (offer?.deal_terms_language && offer.deal_terms_language.trim()) {
    return ` ${offer.deal_terms_language.trim()}`;
  }
  switch (offer?.revenue_base) {
    case "gross":          return " of gross ticket sales";
    case "net_gross":      return " of net ticket sales";
    case "adjusted_gross": return " of ticket sales, minus expenses deducted before split";
    default:               return " of ticket sales, minus mutually agreed upon expenses";
  }
}

function bonusBaseLanguage(offer) {
  switch (offer?.revenue_base) {
    case "gross":          return "of gross ticket sales retro to dollar one";
    case "net_gross":      return "of net ticket sales retro to dollar one";
    case "adjusted_gross": return "of ticket sales retro to dollar one, minus expenses deducted before split";
    default:               return "of the NBOR retro to dollar one, minus mutually agreed upon expenses";
  }
}

function isEditingShowSummary(editedCompCount, editedKillCount, editedDropCount) {
  return editedCompCount !== null || editedKillCount !== null || editedDropCount !== null;
}

function hasShowSummaryOverride(editedCompCount, editedKillCount, editedDropCount, budget) {
  return (editedCompCount !== null && budget.comp_count_override !== null) ||
         (editedKillCount !== null && budget.kill_count_override !== null) ||
         (editedDropCount !== null && budget.drop_count_override !== null);
}

function getEditColumnWidth(
  isSettling,
  requestFormat,
  editedCompCount,
  editedKillCount,
  editedDropCount,
  budget
) {
  const isEditing = isEditingShowSummary(editedCompCount, editedKillCount, editedDropCount);
  const hasOverride = hasShowSummaryOverride(editedCompCount, editedKillCount, editedDropCount, budget);

  if (!isSettling || requestFormat !== "html") return "20px";
  if (isEditing && hasOverride) return "75px";
  if (isEditing && !hasOverride) return "55px";
  return "40px";
}

function showSummaryColgroup(isSettling, requestFormat, isEditing, hasOverride) {
  const editColumnWidth = isSettling && requestFormat === "html" && isEditing && hasOverride ? "75px" :
                          isSettling && requestFormat === "html" && isEditing && !hasOverride ? "55px" :
                          isSettling && requestFormat === "html" ? "40px" : "20px";

  return (
    <colgroup>
      <col span={"4"} />
      <col span={"1"} width={editColumnWidth} />
    </colgroup>
  );
}

function tableColgroup(isSettling, requestFormat, showCurrencyConversion) {
  let innerCols = (
    showCurrencyConversion ? (
      <>
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"15%"} />
      </>
    ) : (
      <>
        <col span={"1"} width={"30%"} />
        <col span={"1"} width={"20%"} />
        <col span={"1"} width={"20%"} />
      </>
    )
  );

  return isSettling && requestFormat === "html" ? (
    <colgroup>
      <col span={"1"} width={"35%"} />
      {innerCols}
      <col span={"1"} width={"5%"} />
    </colgroup>
  ) : (
    <colgroup>
      <col span={"1"} width={"30%"} />
      {innerCols}
      <col span={"1"} width={"0%"} />
    </colgroup>
  );
}

function ticketTableColgroup(isSettling, requestFormat, showCurrencyConversion) {
  let innerCols = (
    showCurrencyConversion ? (
      <>
        <col span={"1"} width={"10%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"15%"} />
      </>
    ) : (
      <>
        <col span={"1"} width={"15%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"5%"} />
        <col span={"1"} width={"20%"} />
        <col span={"1"} width={"20%"} />
      </>
    )
  );

  return isSettling && requestFormat === "html" ? (
    <colgroup>
      <col span={"1"} width={"25%"} />
      {innerCols}
      <col span={"1"} width={"5%"} />
    </colgroup>
  ) : (
    <colgroup>
      <col span={"1"} width={"30%"} />
      {innerCols}
      <col span={"1"} width={"0%"} />
    </colgroup>
  );
}

function summaryTableColgroup(showCurrencyConversion) {
  return (
    showCurrencyConversion ? (
      <colgroup>
        <col span={"1"} width={"70%"} />
        <col span={"1"} width={"20%"} />
        <col span={"1"} width={"10%"} />
      </colgroup>
    ) : (
      <colgroup>
        <col span={"1"} width={"80%"} />
        <col span={"1"} width={"20%"} />
      </colgroup>
    )
  );
}

function finalDropCount(budget, confirm) {
  return (
    budget.drop_count_override !== null ?
      budget.drop_count_override : confirm.number_of_manifested_tickets_checked_in
  );
}

function finalCompCount(budget, confirm) {
  return (
    budget.comp_count_override !== null ?
      budget.comp_count_override : confirm.number_of_tickets_comped
  );
}

// We have no way of automatically determining the real kill count right now
// If it hasn't been overridden, we return 0.
function finalKillCount(budget, confirm) {
  return (
    budget.kill_count_override !== null ?
      budget.kill_count_override : 0
  );
}

const SettlementTable = ({
  budget,
  setBudgetState,
  calculated,
  offer,
  confirm,
  createSettlementLineItem,
  csrfToken,
  team,
  currentUser,
  offerId,
  deleteSettlementLineItem,
  updateSettlementLineItemTaxWithholdingExemption,
  itemToEdit,
  itemToEditChanged,
  deleteSettlementOverride,
  requestFormat,
  isSettling,
  isV3,
  editedDropCount,
  editedDropCountChanged,
  editedCompCount,
  editedCompCountChanged,
  editedKillCount,
  editedKillCountChanged,
  saveOverrides,
  isSaving,
  offerChanged,
  isSavingChanged,
  getActiveTags,
  getAvailableTags,
}) => {
  const [tagManagerOpen, tagManagerOpenChanged] = useState(false);
  const [tagManagerFilter, tagManagerFilterChanged] = useState("");
  const [availableTags, availableTagsChanged] = useState([]);

  formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: team.base_currency_symbol
  });

  const filterBudgetLineItems = (items) => {
    return items.filter((item) => {
      if (requestFormat === "html" && isSettling) {
        return true;
      } else if (isSettling) {
        return (!!item.settlementVisibility
          ? item.settlementVisibility.visible
          : item.show_in_offer) && item.calculatedRightHand > 0;
      } else {
        return item.show_in_offer;
      }
    });
  };

  const groupAndSortBudgetLineItems = (items, budget) => {
    if(budget.from_dollar_one){
      return items;
    }

    const ticketItems = items.filter(item => item.overridable_type === "Ticket");
    const nonTicketItems = items.filter(item => item.overridable_type !== "Ticket");

    const grouped = _.groupBy(ticketItems, (item) =>
      JSON.stringify({
        name: item.name,
        calculatedLeftHand: item.calculatedLeftHand,
        lift_amount: item.lift_amount,
        taxes: item.taxes
      })
    );

    const aggregatedTickets = _.map(grouped, (group) => {
      const first = group[0];
      return {
        ...first,
        calculatedRightHand: _.sumBy(group, 'calculatedRightHand'),
        calculatedRightHandForecast: _.sumBy(group, 'calculatedRightHandForecast'),
        calculatedTotal: _.sumBy(group, 'calculatedTotal'),
        calculatedForecastTotal: _.sumBy(group, 'calculatedForecastTotal')
      };
    });

    const sortedTickets = _.sortBy(aggregatedTickets, (t) => t.position != null ? t.position : 999);
    const allItems = [...sortedTickets, ...nonTicketItems];
    return allItems;
  };

  const filteredAdditions = filterBudgetLineItems(calculated.additionsSection.items);
  const filteredOtherRevenue = filterBudgetLineItems(calculated.revenueSection.items);
  const filteredDeductions = filterBudgetLineItems(calculated.deductionsSection.items);

  const cashBonuses = budget.budget_bonuses
    .filter((bb) => bb.dollar_or_percentage_operator === "CASH")
    .filter((bb) => bb.ticket_count_requirement)
    .filter((bb) => bb.dollar_amount)
    .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement);

  const percentageBonuses = budget.budget_bonuses
    .filter((bb) => bb.dollar_or_percentage_operator === "PERCENTAGE")
    .filter((bb) => bb.ticket_count_requirement)
    .filter((bb) => bb.upside_percentage)
    .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement);

  const { autoSaveOffer, isSavingAttribute } = useSettlementOfferAutosave({
    offer,
    offerChanged,
    isSavingChanged,
    csrfToken,
    url: `/teams/${team.id}/offers/${isV3 ? "v3/" : ""}${offerId}`,
    paramKey: isV3 ? "v3_offer" : "offer",
  });

  const currencySymbol = (
    team.secondary_currency_symbol && team.secondary_currency_symbol.length > 0 ?
      team.secondary_currency_symbol : "USD"
  )

  const globalCurrency = new GlobalCurrency(
    team.currency_conversion_rate, currencySymbol
  );

  const currencyConversionIsEnabled = (
    typeof(team.secondary_currency_symbol) === "string"
      && team.secondary_currency_symbol.length > 0
      && (
        isSettling ?
          offer.display_currency_conversion_for_settlement : offer.display_currency_conversion_for_offer
      )
  );

  const showCurrencyConversion = (
    currencyConversionIsEnabled
      && !team.convert_artist_earnings_only
  );

  const splitPointToBeShared = (
    calculated.showSummary.breakevenThreshold > 0 && calculated.showSummary.breakevenToBeSharedAmount != null ?
      calculated.showSummary.breakevenToBeSharedAmount : null
  );

  const showSummaryRow = (label, amount, isDeduction) => {
    const sign = isDeduction ? "-" : "";

    return (
      <tr key={label}>
        <td
          colSpan={4}
          style={{
            padding: "3px 0px 3px 20px",
          }}
        >
          <table style={{"width": "100%"}}>
            {summaryTableColgroup(showCurrencyConversion)}
            <tbody>
              <tr>
                <td>{label}</td>
                {showCurrencyConversion ? (
                  <td style={{"textAlign": "right"}}>
                    {`${sign}${globalCurrency.formatted(amount)}`}
                  </td>
                ) : null}
                <td style={{"textAlign": "right"}}>
                  {`${sign}${formatCurrency(amount)}`}
                </td>
              </tr>
            </tbody>
          </table>
        </td>
        <td
          colSpan={1}
          style={{
            width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
            textAlign: "center"
          }}
        >
          &nbsp;
        </td>
      </tr>
    );
  };

  const toBeSharedRows = splitPointToBeShared !== null ? (
    <>
      {showSummaryRow("Revenue Before Split Point", calculated.showSummary.breakevenBase, false)}
      {showSummaryRow("Split Point", calculated.showSummary.breakevenThreshold, true)}
      {showSummaryRow("To Be Shared Amount", splitPointToBeShared, false)}
    </>
  ) : showSummaryRow("To Be Shared Amount", calculated.showSummary.toBeSharedAmount, false);

  const simplifiedSupportSettlement = (
    isSettling
      && offer.performer?.act_type === "support"
      && offer.venue_ownership.simplified_support_settlement
  );

  return(<div style={{"padding": "0px 16px 0px 0px"}}>
    <table style={{
            "width": "100%",
            "fontFamily": "Open Sans",
            "fontSize": "14px"
          }}>
      <tbody>
        <tr>
          <td colSpan={2}>
            <table style={{"width": "100%", "borderSpacing": "0px"}}>
              <tbody>
                <tr>
                  {offer.venue_ownership && offer.venue_ownership.logo_thumb_url ? (
                    <td
                      style={{
                        width: "80px",
                        verticalAlign: "top",
                        padding: "0px 15px 10px 0px"
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          overflow: "hidden",
                          borderRadius: "8px"
                        }}
                      >
                        <img
                          src={offer.venue_ownership.logo_thumb_url}
                          style={{
                            "width": "100%",
                            "height": "100%",
                            "objectFit": "cover"
                          }}
                        />
                      </div>
                    </td>
                  ) : null}
                  <td
                    style={{
                      verticalAlign: "top",
                      padding: requestFormat === "pdf" ? "10px 0px" : "2px 0px",
                      textAlign: "left"
                    }}
                  >
                    <p
                      style={{
                        margin: "0px",
                        fontSize: "20px",
                        fontWeight: "bold"
                      }}
                    >
                      {offer.artist_or_event_name}
                    </p>
                    <div
                      style={{
                        margin: "0px",
                        fontSize: "16px",
                        lineHeight: "1.4"
                      }}
                    >
                      {offerDates(offer, confirm, team)}
                      <p style={{ "margin": "0px" }}>
                        {offerLocation(offer)}
                      </p>
                    </div>
                  </td>
                  <td
                    style={{
                      verticalAlign: "top",
                      padding: "10px 0px",
                      textAlign: "right",
                      width: "80px"
                    }}
                  >
                    <div
                      style={{
                        margin: "0px 0px 10px 0px",
                        fontSize: "14px",
                        textAlign: "center",
                        display: "inline-block",
                        background: "var(--od-c-fafafa, #FAFAFA)",
                        border: "1px solid var(--od-c-e9eaeb, #E9EAEB)",
                        borderRadius: "9px",
                        padding: "2px 8px"
                      }}
                    >
                      <span>{isSettling ? "Settlement" : "Offer"}</span>
                    </div>
                    {requestFormat === "html" && isSettling && team.secondary_currency_symbol && team.secondary_currency_symbol.length > 0 ? (
                      <div className="custom-control custom-checkbox custom-checkbox-table text-left" 
                           style={{"marginTop": "10px"}}>
                        <input type="checkbox"
                                className="custom-control-input"
                                checked={offer.display_currency_conversion_for_settlement}
                                disabled={isSavingAttribute('display_currency_conversion_for_settlement')}
                                onChange={(e) => autoSaveOffer({ display_currency_conversion_for_settlement: e.target.checked })}
                                id="offer-display-currency-conversion-for-settlement" />
                        <label className="custom-control-label"
                                style={{"fontSize": "14px", "color": "var(--od-c-414651, #414651)"}}
                                htmlFor="offer-display-currency-conversion-for-settlement">
                          Display currency conversion<br />
                          {`(1 ${team.base_currency_symbol} / ${team.currency_conversion_rate} ${team.secondary_currency_symbol})`}
                        </label>
                      </div>
                    ) : null}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        {!isSettling ? (
          <>
            {currentUser && currentUser.can_read_tagged_items && requestFormat === "html" ? (
              <tr>
                <td colSpan={2}>
                  <TagManagerApp
                    csrfToken={csrfToken}
                    team={team}
                    currentUser={currentUser}
                    scope="Offer"
                    title="Tags for Offer"
                    taggable={offer}
                  />
                </td>
              </tr>
            ) : null}
            <tr>
              <td colSpan={2}>
                <div
                  style={{
                    margin: "0px -20px"
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      marginTop: "5px",
                      borderCollapse: "separate",
                      borderSpacing: "20px 0px"
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            width: "50%",
                            verticalAlign: "top",
                            border: "1px solid var(--od-c-e0e0e0, #E0E0E0)",
                            borderRadius: "8px",
                            background: "var(--od-c-ffffff, #FFFFFF)",
                            padding: "0px"
                          }}
                        >
                          <div
                            style={{
                              margin: "12px 16px 0px 16px",
                              background: "var(--od-c-ffffff, #FFFFFF)"
                            }}
                          >
                            <p
                              style={{
                                margin: "0 0 2px 0",
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "var(--od-c-686868, #666666)"
                              }}
                            >
                              Deal Terms
                            </p>
                          </div>
                          <div
                            style={{
                              margin: "0px 16px 16px 16px",
                              background: "var(--od-c-ffffff, #FFFFFF)"
                            }}
                          >
                            {!budget.from_dollar_one && budget.deal_brackets && budget.deal_brackets.length > 0 ? (
                              <>
                                {budget.deal_brackets
                                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                                  .map((dealBracket, index) => (
                                    <p
                                      key={dealBracket.id}
                                      style={{
                                        fontSize: "16px",
                                        marginTop: index === 0 ? "0px" : "12px",
                                        marginBottom: "0px",
                                        lineHeight: "1.4"
                                      }}
                                    >
                                      {dealBracket.quantity && dealBracket.quantity > 0 ? (
                                        <>
                                          <span style={{fontSize: "14px"}}>
                                            {index === 0 ? 'First' : 'Next'} {parseInt(dealBracket.quantity || 0).toLocaleString()} tickets:{" "}
                                          </span>
                                          <br />
                                        </>
                                      ) : null}
                                      {team?.door_deal_label_enabled && parseFloat(dealBracket.dollar_amount || 0) === 0 && parseFloat(dealBracket.upside_percentage || 0) > 0 ? (
                                        <>
                                          <strong
                                            style={{
                                              fontSize: "18px",
                                              fontWeight: "700"
                                            }}
                                          >
                                            {(team?.door_deal_label_text || "Door Deal") + " — " + dealBracket.upside_percentage + "%"}
                                          </strong>
                                          <span style={{ color: "var(--od-c-686868, #666666)" }}>
                                            {dealLanguage(offer, true)}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <strong
                                            style={{
                                              fontSize: "18px",
                                              fontWeight: "700"
                                            }}
                                          >
                                            {formatCurrency(dealBracket.dollar_amount || "0.00")}
                                          </strong>
                                          {parseFloat(dealBracket.upside_percentage || 0) > 0 ? (
                                            <>
                                              <span style={{ margin: "0px 4px" }}>
                                                {dealBracket.dollar_or_percentage_operator === "AND" ? "+" : "VS"}
                                              </span>
                                              <strong
                                                style={{
                                                  fontSize: "18px",
                                                  fontWeight: "700"
                                                }}
                                              >
                                                {dealBracket.upside_percentage || "0"}%
                                              </strong>
                                            </>
                                          ) : null}
                                          <span style={{ color: "var(--od-c-686868, #666666)" }}>
                                            {dealLanguage(offer, parseFloat(dealBracket.upside_percentage || 0) > 0)}
                                          </span>
                                        </>
                                      )}
                                    </p>
                                  ))}
                              </>
                            ) : (
                              <>
                                <p
                                  style={{
                                    fontSize: "16px",
                                    marginTop: "0px",
                                    marginBottom: "0px",
                                    lineHeight: "1.4"
                                  }}
                                >
                                  <strong
                                    style={{
                                      fontSize: "18px",
                                      fontWeight: "700"
                                    }}
                                  >
                                    {team?.door_deal_label_enabled && parseFloat(budget.dollar_amount || 0) === 0 && parseFloat(budget.upside_percentage || 0) > 0 ? (
                                      <>
                                        {(team?.door_deal_label_text || "Door Deal") + " — " + (budget.upside_percentage || "0") + "%"}
                                      </>
                                    ) : (
                                      <>
                                        {formatCurrency(budget.dollar_amount || "0.00")}
                                        {currencyConversionIsEnabled ? (
                                          ` (${globalCurrency.formatted(budget.dollar_amount)} ${team.secondary_currency_symbol})`
                                        ) : null}
                                      </>
                                    )}
                                  </strong>
                                  {!(team?.door_deal_label_enabled && parseFloat(budget.dollar_amount || 0) === 0 && parseFloat(budget.upside_percentage || 0) > 0) && hasUpsidePercentage(budget) ? (
                                    <>
                                      <span style={{ margin: "0px 4px" }}>
                                        {budget.dollar_or_percentage_operator === "AND" ? "+" : "VS"}
                                      </span>
                                      <strong
                                        style={{
                                          fontSize: "18px",
                                          fontWeight: "700"
                                        }}
                                      >
                                        {budget.upside_percentage || "0"}%
                                      </strong>
                                    </>
                                  ) : null}
                                  <span style={{ color: "var(--od-c-686868, #666666)" }}>
                                    {dealLanguage(offer, hasUpsidePercentage(budget))}
                                  </span>
                                </p>
                                {budget.guarantee_notes?.trim() && (
                                  <p style={{ margin: '0px 0px 4px 0px', fontSize: '16px', color: 'var(--od-c-686868, #666666)', lineHeight: '1.4' }}>
                                    {budget.guarantee_notes.trim()}
                                  </p>
                                )}
                                {cashBonuses.length === 1 ? (
                                  <p
                                    style={{
                                      fontSize: "14px",
                                      marginTop: "10px",
                                      marginBottom: "0px"
                                    }}
                                  >
                                    {
                                      cashBonuses
                                        .map((b) => `At ${b.ticket_count_requirement.toLocaleString()} tickets, a ${formatter.format(b.dollar_amount)} bonus will be paid.`)
                                        .join(" ")
                                    }
                                  </p>
                                ) : cashBonuses.length > 1 ? (
                                  <ol
                                    style={{
                                      fontSize: "14px",
                                      marginTop: "10px",
                                      marginBottom: "0px"
                                    }}
                                  >
                                    {cashBonuses.map((b, index) => (
                                      <li
                                        key={index}
                                      >
                                        {`At ${b.ticket_count_requirement.toLocaleString()} tickets, a ${formatter.format(b.dollar_amount)} bonus will be paid.`}
                                      </li>
                                    ))}
                                  </ol>
                                ) : null}
                                {percentageBonuses.length === 1 ? (
                                  <p
                                    style={{
                                      fontSize: "14px",
                                      marginTop: "10px",
                                      marginBottom: "0px"
                                    }}
                                  >
                                    {
                                      percentageBonuses
                                        .map((b, index) => {
                                          var nextBonus = percentageBonuses[index + 1];
                                          var endOfSentence = ".";

                                          if(nextBonus){
                                            endOfSentence = ` up to ${(nextBonus.ticket_count_requirement - 1).toLocaleString()} tickets.`;
                                          }

                                          return `At ${b.ticket_count_requirement.toLocaleString()} tickets sold, the deal converts to ${parseFloat(b.upside_percentage).toFixed(2)}% ${bonusBaseLanguage(offer)}${endOfSentence}`
                                        })
                                        .join(" ")
                                    }
                                  </p>
                                ) : percentageBonuses.length > 1 ? (
                                  <ol
                                    style={{
                                      fontSize: "14px",
                                      marginTop: "10px",
                                      marginBottom: "0px"
                                    }}
                                  >
                                    {percentageBonuses
                                      .map((b, index) => {
                                        var nextBonus = percentageBonuses[index + 1];
                                        var endOfSentence = ".";

                                        if(nextBonus){
                                          endOfSentence = ` up to ${(nextBonus.ticket_count_requirement - 1).toLocaleString()} tickets.`;
                                        }

                                        return (
                                          <li
                                            key={index}
                                          >
                                            {`At ${b.ticket_count_requirement.toLocaleString()} tickets sold, the deal converts to ${parseFloat(b.upside_percentage).toFixed(2)}% ${bonusBaseLanguage(offer)}${endOfSentence}`}
                                          </li>
                                        )
                                      })}
                                  </ol>
                                ) : null}
                              </>
                            )}
                            {calculated.showSummary?.breakevenThreshold > 0 ? (() => {
                              const threshold = calculated.showSummary.breakevenThreshold;
                              const gatedBase = calculated.showSummary.breakevenBase || 0;
                              const diff = gatedBase - threshold;
                              const met = diff >= 0;
                              return (
                                <div style={{
                                  margin: "8px 0 0 0",
                                  padding: "6px 10px",
                                  background: met ? "#dafbe1" : "#fff8c5",
                                  borderLeft: `3px solid ${met ? "#1a7f37" : "#d4a72c"}`,
                                  borderRadius: "0 4px 4px 0",
                                  fontSize: "12px",
                                  color: "#57606a"
                                }}>
                                  <strong>Split point {met ? "met" : "short"}</strong>
                                  {" — "}
                                  {met
                                    ? `revenue exceeded split point by ${formatCurrency(diff)}`
                                    : `${formatCurrency(Math.abs(diff))} below ${formatCurrency(threshold)} split point`}
                                </div>
                              );
                            })() : null}
                            {(() => {
                              const earnings = calculated.artistEarningsActual || calculated.artistEarnings;
                              return earnings.capOverflow > 0 ? (
                                <div style={{
                                  margin: "8px 0 0 0",
                                  padding: "6px 10px",
                                  background: "#fff8c5",
                                  borderLeft: "3px solid #d4a72c",
                                  borderRadius: "0 4px 4px 0",
                                  fontSize: "12px",
                                  color: "#57606a"
                                }}>
                                  <strong>Cap overflow:</strong>{" "}
                                  {formatCurrency(earnings.capOverflow)}{" "}
                                  {earnings.capOverflowTarget
                                    ? `routed to ${earnings.capOverflowTarget === "headliner" ? "Headliner" : "Venue"}`
                                    : "(no target — excess not paid)"}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </td>
                        <td
                          style={{
                            width: "50%",
                            verticalAlign: "top",
                            border: "1px solid var(--od-c-e0e0e0, #E0E0E0)",
                            borderRadius: "8px",
                            background: "var(--od-c-ffffff, #FFFFFF)",
                            padding: "0px"
                          }}
                        >
                          <div
                            style={{
                              margin: "12px 16px 0px 16px",
                              background: "var(--od-c-ffffff, #FFFFFF)"
                            }}
                          >
                            <p
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "var(--od-c-686868, #666666)"
                              }}
                            >
                              Potential Artist Earnings
                            </p>
                          </div>
                          <div
                            style={{
                              margin: "0px 16px 16px 16px",
                              background: "var(--od-c-ffffff, #FFFFFF)"
                            }}
                          >
                            <p
                              style={{
                                margin: "0px",
                                fontSize: "18px",
                                fontWeight: "700",
                                color: "var(--od-c-333333, #333333)"
                              }}
                            >
                              {formatCurrency(calculated.artistEarnings.artistSettlementDue)}{" "}
                              {currencyConversionIsEnabled ? (
                                `(${globalCurrency.formatted(calculated.artistEarnings.artistSettlementDue)} ${team.secondary_currency_symbol})`
                              ) : null}
                            </p>
                            {(() => {
                              const earnings = calculated.artistEarningsActual || calculated.artistEarnings;
                              return (
                                <>
                                  {earnings.payoutCapped ? (
                                    <div style={{
                                      margin: "8px 0",
                                      padding: "6px 10px",
                                      background: "#fff8c5",
                                      borderLeft: "3px solid #d4a72c",
                                      borderRadius: "0 4px 4px 0",
                                      fontSize: "12px",
                                      color: "#57606a"
                                    }}>
                                      <strong>Capped</strong> — calculated {formatCurrency(earnings.rawPayout)}, reduced by {formatCurrency(earnings.rawPayout - earnings.artistGuaranteeAndOrPercentage)}
                                    </div>
                                  ) : null}
                                  {earnings.payoutFloored ? (
                                    <div style={{
                                      margin: "8px 0",
                                      padding: "6px 10px",
                                      background: "#ddf4ff",
                                      borderLeft: "3px solid #54aeff",
                                      borderRadius: "0 4px 4px 0",
                                      fontSize: "12px",
                                      color: "#57606a"
                                    }}>
                                      <strong>Floored</strong> — calculated {formatCurrency(earnings.rawPayout)}, increased by {formatCurrency(earnings.artistGuaranteeAndOrPercentage - earnings.rawPayout)}
                                    </div>
                                  ) : null}
                                </>
                              );
                            })()}
                            {calculated.additionsSection.items.length  ? (
                              <p className="my-1">
                                Total Additions:{" "}
                                <strong>
                                  {formatCurrency(calculated.artistEarnings.totalAdditions)}{" "}
                                  {currencyConversionIsEnabled ? (
                                    `(${globalCurrency.formatted(calculated.artistEarnings.totalAdditions)} ${team.secondary_currency_symbol})`
                                  ) : null}
                                </strong>
                              </p>
                            ) : null}
                            {calculated.deductionsSection.items.length  ? (
                              <p className="my-1">
                                Total Deductions:{" "}
                                <strong>
                                  {formatCurrency(calculated.artistEarnings.totalDeductions)}{" "}
                                  {currencyConversionIsEnabled ? (
                                    `(${globalCurrency.formatted(calculated.artistEarnings.totalDeductions)} ${team.secondary_currency_symbol})`
                                  ) : null}
                                </strong>
                              </p>
                            ) : null}
                            {budget.has_tax_withholding ? (
                              <p className="my-1">
                                {`Tax Withholding (${budget.tax_withholding_percentage}% of taxable artist earnings): `}
                                <strong>
                                  {formatCurrency(calculated.artistEarnings.taxWithholding)}{" "}
                                  {currencyConversionIsEnabled ? (
                                    `(${globalCurrency.formatted(calculated.artistEarnings.taxWithholding)} ${team.secondary_currency_symbol})`
                                  ) : null}
                                </strong>
                              </p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </>
        ) : null}
        {requestFormat === "html" || !isSettling || filterBudgetLineItems(calculated.ticketsSection.items).length > 0 ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "18px"}}>
                <BudgetLineItemsTable items={
                                        groupAndSortBudgetLineItems(
                                          filterBudgetLineItems(calculated.ticketsSection.items),
                                          budget
                                        )
                                      }
                                      title={"Tickets"}
                                      tableColgroup={isSettling ? (
                                        tableColgroup(isSettling, requestFormat, showCurrencyConversion)
                                      ) : (
                                        ticketTableColgroup(isSettling, requestFormat, showCurrencyConversion)
                                      )}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Tickets"
                                          });
                                        }
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"PRICE"}
                                      rightHandTitle={"SOLD"}
                                      totalTitle={"GROSS"}
                                      totalRowLeftHand={"Total"}
                                      totalRowRightHand={numberWithCommas(calculated.ticketsSection.rightTotal)}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      totalRowTotal={calculated.ticketsSection.total}>
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        {filteredAdditions.length > 0 || (isSettling && requestFormat === "html") ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "12px"}}>
                <BudgetLineItemsTable items={groupAndSortBudgetLineItems(filteredAdditions, budget)}
                                      title={"Additions"}
                                      tableColgroup={tableColgroup(isSettling, requestFormat, showCurrencyConversion)}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Additions"
                                          });
                                        }
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"RATE"}
                                      rightHandTitle={"QUANTITY"}
                                      totalTitle={"TOTAL"}
                                      totalRowLeftHand={"Total"}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      onToggleTaxWithholdingExemption={isV3 ? updateSettlementLineItemTaxWithholdingExemption : undefined}
                                      totalRowTotal={calculated.additionsSection.total}>
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        {filteredOtherRevenue.length > 0 || (isSettling && requestFormat === "html") ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "3px"}}>
                <BudgetLineItemsTable items={filteredOtherRevenue}
                                      title={"Other Revenue"}
                                      tableColgroup={tableColgroup(isSettling, requestFormat, showCurrencyConversion)}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Revenue"
                                          });
                                        }
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"RATE"}
                                      rightHandTitle={"QUANTITY"}
                                      totalTitle={"GROSS"}
                                      totalRowLeftHand={"Total"}
                                      totalRowRightHand={numberWithCommas(calculated.revenueSection.rightTotal)}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      totalRowTotal={calculated.revenueSection.total}>
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        {requestFormat === "html" || !isSettling || filterBudgetLineItems(calculated.beforeAdjustedDeductionsSection.items).length > 0 ? (
          <tr>
            <td colSpan={2}>
              <div>
                <BudgetLineItemsTable items={filterBudgetLineItems(calculated.beforeAdjustedDeductionsSection.items)}
                                      title={"Before Adjusted Deductions"}
                                      tableColgroup={tableColgroup(isSettling, requestFormat, showCurrencyConversion)}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Before Adjusted Deductions"
                                          });
                                        }
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"RATE"}
                                      rightHandTitle={"AMOUNT"}
                                      totalTitle={"TOTAL"}
                                      totalRowLeftHand={"Total"}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      totalRowTotal={calculated.beforeAdjustedDeductionsSection.total}>
                  <tr>
                    <td colSpan={3}
                        style={{
                          "textAlign": "right",
                          "padding": "3px 9px 3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      <strong style={{"fontSize": "14px"}}>
                        Subtotal
                      </strong>
                    </td>
                    {showCurrencyConversion ? (
                      <td colSpan={1}
                          style={{
                            "textAlign": "right",
                            "padding": "3px 9px 3px 0px",
                            "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                          }}>
                        <strong style={{"fontSize": "14px"}}>
                          {globalCurrency.formatted(calculated.showSummary.netBoxOfficeReceipts)}
                        </strong>
                      </td>
                    ) : null}
                    <td colSpan={1}
                        style={{
                          "textAlign": "right",
                          "padding": "3px 15px 3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      <strong style={{"fontSize": "14px"}}>
                        {formatCurrency(calculated.showSummary.netBoxOfficeReceipts)}
                      </strong>
                    </td>
                    <td style={{
                          "textAlign": "right",
                          "padding": "3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      &nbsp;
                    </td>
                  </tr>
                  {calculated.showSummary.admissionTax > 0 ? (
                    <tr data-testid="settlement-admission-tax">
                      <td colSpan={3}
                          style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
                        <span style={{"fontSize": "14px"}}>
                          Admission Tax (added on top)
                        </span>
                      </td>
                      {showCurrencyConversion ? (
                        <td colSpan={1}
                            style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
                          <span style={{"fontSize": "14px"}}>
                            {globalCurrency.formatted(calculated.showSummary.admissionTax)}
                          </span>
                        </td>
                      ) : null}
                      <td colSpan={1}
                          style={{"textAlign": "right", "padding": "3px 15px 3px 0px"}}>
                        <span style={{"fontSize": "14px"}}>
                          {formatCurrency(calculated.showSummary.admissionTax)}
                        </span>
                      </td>
                      <td style={{"textAlign": "right", "padding": "3px 0px"}}>
                        &nbsp;
                      </td>
                    </tr>
                  ) : null}
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        {requestFormat === "html" || !isSettling || filterBudgetLineItems(calculated.expensesSection.items.concat(calculated.finalExpensesSection.items)).length > 0 ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "3px"}}>
                <BudgetLineItemsTable items={filterBudgetLineItems(calculated.expensesSection.items.concat(calculated.finalExpensesSection.items))}
                                      title={"Expenses"}
                                      tableColgroup={tableColgroup(isSettling, requestFormat, showCurrencyConversion)}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Expenses"
                                          });
                                        }
                                      }
                                      settlementLineItems={
                                        budget.settlement_line_items.filter((sli) => sli.section === "Revenue")
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"RATE"}
                                      rightHandTitle={"QUANTITY"}
                                      totalTitle={"TOTAL"}
                                      totalRowLeftHand={"Total"}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      totalRowTotal={calculated.expensesSection.total + calculated.finalExpensesSection.total}>
                  <tr>
                    <td colSpan={3}
                        style={{
                          "textAlign": "right",
                          "padding": "3px 9px 3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      <strong style={{"fontSize": "14px"}}>
                        Subtotal
                      </strong>
                    </td>
                    {showCurrencyConversion ? (
                      <td colSpan={1}
                          style={{
                            "textAlign": "right",
                            "padding": "3px 9px 3px 0px",
                            "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                          }}>
                        <strong style={{"fontSize": "14px"}}>
                          {`-${globalCurrency.formatted(calculated.expensesSection.total)}`}
                        </strong>
                      </td>
                    ) : null}
                    <td colSpan={1}
                        style={{
                          "textAlign": "right",
                          "padding": "3px 15px 3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      <strong style={{"fontSize": "14px"}}>
                        {`-${formatCurrency(calculated.expensesSection.total + calculated.finalExpensesSection.total)}`}
                      </strong>
                    </td>
                    <td style={{
                          "textAlign": "right",
                          "padding": "3px 0px",
                          "borderTop": "2px solid var(--od-c-d5d7da, #D5D7DA)"
                        }}>
                      &nbsp;
                    </td>
                  </tr>
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        {!isSettling && budget.has_merchandising ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "8px"}}>
                <table style={{"width": "100%", "borderSpacing": "0px 2px", "borderCollapse": "separate"}}>
                  <colgroup>
                    <col span={"1"} width={"15%"} />
                    <col span={"1"} width={"15%"} />
                    <col span={"1"} width={"40%"} />
                    <col span={"1"} width={"15%"} />
                    <col span={"1"} width={"15%"} />
                  </colgroup>
                  <thead style={{"textAlign": "left"}}>
                    <tr style={{"background": "var(--od-c-f5f5f5, #F5F5F5)"}}>
                      <th style={{
                        "fontSize": "14px",
                        "color": "var(--od-c-000000, #000000)",
                        "padding": "6px 15px",
                        "textAlign": "left"
                      }}>
                        <strong>Merch Split</strong>
                      </th>
                      <th style={{
                        "fontWeight": "600",
                        "fontSize": "14px",
                        "color": "var(--od-c-999999, #999999)",
                        "textAlign": "left",
                        "padding": "6px 15px 6px 0px"
                      }}>
                        SELLER
                      </th>
                      <th style={{
                        "fontWeight": "600",
                        "fontSize": "14px",
                        "color": "var(--od-c-999999, #999999)",
                        "textAlign": "left",
                        "padding": "6px 15px 6px 0px"
                      }}>
                        NOTES
                      </th>
                      <th style={{
                        "fontWeight": "600",
                        "fontSize": "14px",
                        "color": "var(--od-c-999999, #999999)",
                        "textAlign": "right",
                        "padding": "6px 15px 6px 0px"
                      }}>
                        ARTIST
                      </th>
                      <th style={{
                        "fontWeight": "600",
                        "fontSize": "14px",
                        "color": "var(--od-c-999999, #999999)",
                        "textAlign": "right",
                        "padding": "6px 15px 6px 0px"
                      }}>
                        VENUE
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{"fontSize": "14px"}}>
                    <tr>
                      <td style={{"padding": "3px 15px"}}>
                        Soft Merch
                      </td>
                      <td style={{"padding": "3px 15px"}}>
                        {budget.soft_merch_sold_by}
                      </td>
                      <td style={{"padding": "3px 15px"}}>
                        {budget.soft_merch_notes}
                      </td>
                      <td style={{"padding": "3px 15px", "textAlign": "right"}}>
                        {parseFloat(budget.soft_merch_rate) || 0}%
                      </td>
                      <td style={{"padding": "3px 15px", "textAlign": "right"}}>
                        {100.0 - parseFloat(budget.soft_merch_rate) || 0}%
                      </td>
                    </tr>
                    <tr>
                      <td style={{"padding": "3px 15px"}}>
                        Hard Merch
                      </td>
                      <td style={{"padding": "3px 15px"}}>
                        {budget.hard_merch_sold_by}
                      </td>
                      <td style={{"padding": "3px 15px"}}>
                        {budget.hard_merch_notes}
                      </td>
                      <td style={{"padding": "3px 15px", "textAlign": "right"}}>
                        {parseFloat(budget.hard_merch_rate) || 0}%
                      </td>
                      <td style={{"padding": "3px 15px", "textAlign": "right"}}>
                        {100.0 - parseFloat(budget.hard_merch_rate) || 0}%
                      </td>
                    </tr>
                    {budget.addl_merch_notes ? (
                      <tr>
                        <td colSpan="5" style={{"padding": "3px 15px"}}>
                          Additional notes: {budget.addl_merch_notes}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        ) : null}
        {requestFormat === "html" || !simplifiedSupportSettlement ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "15px"}}>
                <table
                  style={{
                    width: "100%",
                    borderSpacing: "0px",
                    background: "var(--od-c-f5f5f5, #F5F5F5)",
                    borderRadius: "8px",
                    overflow: "hidden"
                  }}
                >
                  {showSummaryColgroup(
                    isSettling,
                    requestFormat,
                    isEditingShowSummary(editedCompCount, editedKillCount, editedDropCount),
                    hasShowSummaryOverride(editedCompCount, editedKillCount, editedDropCount, budget)
                  )}
                  <tbody style={{"fontSize": "14px"}}>
                    <tr>
                      <td
                        style={{
                          padding: "20px 0px 5px 20px",
                        }}
                        colSpan={4}
                      >
                        <table style={{width: "100%"}}>
                          <colgroup>
                            {currencyConversionIsEnabled ? (
                              <>
                                <col span="1" width="70%" />
                                <col span="1" width="20%" />
                                <col span="1" width="10%" />
                              </>
                            ) : (
                              <col span="3" width="100%" />
                            )}
                          </colgroup>
                          <tbody>
                            <tr>
                              <td>
                                <strong style={{"fontSize": "16px"}}>
                                  Show Summary
                                </strong>
                              </td>
                              {currencyConversionIsEnabled ? (
                                <>
                                  <td className="text-right">
                                    <strong style={{"fontSize": "14px"}}>
                                      {team.secondary_currency_symbol}
                                    </strong>
                                  </td>
                                  <td className="text-right">
                                    <strong style={{"fontSize": "14px"}}>
                                      TOTAL
                                    </strong>
                                  </td>
                                </>
                              ) : null}
                            </tr>
                          </tbody>
                        </table>
                      </td>
                      <td
                        colSpan={1}
                        style={{
                          width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                          textAlign: "center"
                        }}
                      >
                        &nbsp;
                      </td>
                    </tr>
                    {isSettling ? (
                      <>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Total Tickets Sold</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      &nbsp;
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {numberWithCommas(calculated.ticketsSection.rightTotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Comps</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      &nbsp;
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {editedCompCount !== null ? (
                                      <MaskedInput style={{
                                                      "fontSize": "12px",
                                                      "border": "none",
                                                      "width": "100%",
                                                      "padding": "3px 0px",
                                                      "textAlign": "right",
                                                      "background": "var(--od-c-ffffff, #fff)",
                                                      "color": "var(--od-c-000000, #000)"
                                                    }}
                                                    autoFocus={true}
                                                    mask={createNumberMask({
                                                      prefix: "",
                                                      decimalLimit: 0
                                                    })}
                                                    onChange={
                                                      (e) => {
                                                        editedCompCountChanged(e.target.value);
                                                      }
                                                    }
                                                    value={editedCompCount || ""} />
                                    ) : (
                                      numberWithCommas(finalCompCount(budget, confirm))
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            {requestFormat === "html" ? (
                              <div>
                                {editedCompCount !== null ? (
                                  <>
                                    <a href="#"
                                        style={{"color": "var(--od-c-999999, #999999)", "marginRight": "3px"}}
                                        title={"Cancel"}
                                        onClick={
                                          (e) => {
                                            e.preventDefault();
                                            editedCompCountChanged(null);
                                          }
                                        }>
                                      <i aria-hidden className="fas fa-times-circle fa-fw"></i>
                                    </a>
                                    {budget.comp_count_override !== null ? (
                                      <a href="#"
                                          style={{"color": "var(--od-c-ff595e, #FF595E)", "marginRight": "3px"}}
                                          title={"Delete"}
                                          onClick={
                                            (e) => {
                                              e.preventDefault();
                                              saveOverrides(csrfToken, team, offerId, budget, { comp_count_override: "" });
                                            }
                                          }>
                                        <i aria-hidden className="fas fa-trash-alt fa-fw"></i>
                                      </a>
                                    ) : null}
                                    <button style={{
                                              "border": "none",
                                              "cursor": "pointer",
                                              "padding": "0px",
                                            }}
                                            title={"Save"}
                                            type="submit">
                                      <i aria-hidden
                                          style={{"color": "var(--od-c-1982c4, #1982C4)"}}
                                          className="fas fa-check-circle fa-fw"></i>
                                    </button>
                                  </>
                                ) : (
                                  <a href="#"
                                      onClick={
                                        (e) => {
                                          e.preventDefault();

                                          var compCount = finalCompCount(budget, confirm);
                                          editedCompCountChanged(compCount.toString());
                                          itemToEditChanged({});
                                        }
                                      }
                                      style={{"color": "var(--od-c-999999, #999999)"}}>
                                    <i aria-hidden className="fa fa-pencil fa-fw"></i>
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Kills</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      &nbsp;
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {editedKillCount !== null ? (
                                      <MaskedInput style={{
                                                      "fontSize": "12px",
                                                      "border": "none",
                                                      "width": "100%",
                                                      "padding": "3px 0px",
                                                      "textAlign": "right",
                                                      "background": "var(--od-c-ffffff, #fff)",
                                                      "color": "var(--od-c-000000, #000)"
                                                    }}
                                                    autoFocus={true}
                                                    mask={createNumberMask({
                                                      prefix: "",
                                                      decimalLimit: 0
                                                    })}
                                                    onChange={
                                                      (e) => {
                                                        editedKillCountChanged(e.target.value);
                                                      }
                                                    }
                                                    value={editedKillCount || ""} />
                                    ) : (
                                      numberWithCommas(finalKillCount(budget, confirm))
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            {requestFormat === "html" ? (
                              <div>
                                {editedKillCount !== null ? (
                                  <>
                                    <a href="#"
                                        style={{"color": "var(--od-c-999999, #999999)", "marginRight": "3px"}}
                                        title={"Cancel"}
                                        onClick={
                                          (e) => {
                                            e.preventDefault();
                                            editedKillCountChanged(null);
                                          }
                                        }>
                                      <i aria-hidden className="fas fa-times-circle fa-fw"></i>
                                    </a>
                                    {budget.kill_count_override !== null ? (
                                      <a href="#"
                                          style={{"color": "var(--od-c-ff595e, #FF595E)", "marginRight": "3px"}}
                                          title={"Delete"}
                                          onClick={
                                            (e) => {
                                              e.preventDefault();
                                              saveOverrides(csrfToken, team, offerId, budget, { kill_count_override: "" });
                                            }
                                          }>
                                        <i aria-hidden className="fas fa-trash-alt fa-fw"></i>
                                      </a>
                                    ) : null}
                                    <button style={{
                                              "border": "none",
                                              "cursor": "pointer",
                                              "padding": "0px",
                                            }}
                                            title={"Save"}
                                            type="submit">
                                      <i aria-hidden
                                          style={{"color": "var(--od-c-1982c4, #1982C4)"}}
                                          className="fas fa-check-circle fa-fw"></i>
                                    </button>
                                  </>
                                ) : (
                                  <a href="#"
                                      onClick={
                                        (e) => {
                                          e.preventDefault();

                                          var killCount = finalKillCount(budget, confirm);
                                          editedKillCountChanged(killCount.toString());
                                          itemToEditChanged({});
                                        }
                                      }
                                      style={{"color": "var(--od-c-999999, #999999)"}}>
                                    <i aria-hidden className="fa fa-pencil fa-fw"></i>
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Drop Count</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      &nbsp;
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {editedDropCount !== null ? (
                                      <MaskedInput style={{
                                                      "fontSize": "12px",
                                                      "border": "none",
                                                      "width": "100%",
                                                      "padding": "3px 0px",
                                                      "textAlign": "right",
                                                      "background": "var(--od-c-ffffff, #fff)",
                                                      "color": "var(--od-c-000000, #000)"
                                                    }}
                                                    autoFocus={true}
                                                    mask={createNumberMask({
                                                      prefix: "",
                                                      decimalLimit: 0
                                                    })}
                                                    onChange={
                                                      (e) => {
                                                        editedDropCountChanged(e.target.value);
                                                      }
                                                    }
                                                    value={editedDropCount || ""} />
                                    ) : (
                                      numberWithCommas(finalDropCount(budget, confirm))
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            {requestFormat === "html" ? (
                              <div>
                                {editedDropCount !== null ? (
                                  <>
                                    <a href="#"
                                        style={{"color": "var(--od-c-999999, #999999)", "marginRight": "3px"}}
                                        title={"Cancel"}
                                        onClick={
                                          (e) => {
                                            e.preventDefault();
                                            editedDropCountChanged(null);
                                          }
                                        }>
                                      <i aria-hidden className="fas fa-times-circle fa-fw"></i>
                                    </a>
                                    {budget.drop_count_override !== null ? (
                                      <a href="#"
                                          style={{"color": "var(--od-c-ff595e, #FF595E)", "marginRight": "3px"}}
                                          title={"Delete"}
                                          onClick={
                                            (e) => {
                                              e.preventDefault();
                                              saveOverrides(csrfToken, team, offerId, budget, { drop_count_override: "" });
                                            }
                                          }>
                                        <i aria-hidden className="fas fa-trash-alt fa-fw"></i>
                                      </a>
                                    ) : null}
                                    <button style={{
                                              "border": "none",
                                              "cursor": "pointer",
                                              "padding": "0px",
                                            }}
                                            title={"Save"}
                                            type="submit">
                                      <i aria-hidden
                                          style={{"color": "var(--od-c-1982c4, #1982C4)"}}
                                          className="fas fa-check-circle fa-fw"></i>
                                    </button>
                                  </>
                                ) : (
                                  <a href="#"
                                      onClick={
                                        (e) => {
                                          e.preventDefault();

                                          var dropCount = finalDropCount(budget, confirm);
                                          editedDropCountChanged(dropCount.toString());
                                          itemToEditChanged({});
                                        }
                                      }
                                      style={{"color": "var(--od-c-999999, #999999)"}}>
                                    <i aria-hidden className="fa fa-pencil fa-fw"></i>
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        </tr>
                      </>
                    ) : null}
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: "3px 0px 3px 20px",
                        }}
                      >
                        <table style={{"width": "100%"}}>
                          {summaryTableColgroup(showCurrencyConversion)}
                          <tbody>
                            <tr>
                              <td>Net Box Office Receipts</td>
                              {showCurrencyConversion ? (
                                <td style={{"textAlign": "right"}}>
                                  {globalCurrency.formatted(calculated.showSummary.netBoxOfficeReceipts)}
                                </td>
                              ) : null}
                              <td style={{"textAlign": "right"}}>
                                {formatCurrency(calculated.showSummary.netBoxOfficeReceipts)}
                              </td>
                            </tr>
                            {calculated.showSummary.admissionTax > 0 ? (
                              <tr data-testid="show-summary-admission-tax">
                                {/* Nested-table rows sit tighter than the outer
                                    summary rows, so pad to keep the rhythm. */}
                                <td style={{"paddingTop": "6px"}}>Admission Tax (added on top)</td>
                                {showCurrencyConversion ? (
                                  <td style={{"textAlign": "right", "paddingTop": "6px"}}>
                                    {globalCurrency.formatted(calculated.showSummary.admissionTax)}
                                  </td>
                                ) : null}
                                <td style={{"textAlign": "right", "paddingTop": "6px"}}>
                                  {formatCurrency(calculated.showSummary.admissionTax)}
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </td>
                      <td
                        colSpan={1}
                        style={{
                          width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                          textAlign: "center"
                        }}
                      >
                        &nbsp;
                      </td>
                    </tr>
                    {calculated.expensesSection.total > 0 ? (
                      <>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Expenses</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {`-${globalCurrency.formatted(calculated.showSummary.expenses)}`}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {`-${formatCurrency(calculated.showSummary.expenses)}`}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Show Net, After Expenses</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {globalCurrency.formatted(calculated.showSummary.showNetAfterExpenses)}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {formatCurrency(calculated.showSummary.showNetAfterExpenses)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      </>
                    ) : null}
                    {calculated.finalExpensesSection.total > 0 ? (
                      <>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Final Expenses</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {`-${globalCurrency.formatted(calculated.finalExpensesSection.total)}`}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {`-${formatCurrency(calculated.finalExpensesSection.total)}`}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Show Net, After Final Expenses</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {globalCurrency.formatted(calculated.showSummary.showNetAfterFinalExpenses)}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {formatCurrency(calculated.showSummary.showNetAfterFinalExpenses)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      </>
                    ) : null}
                    {calculated.showSummary.promoterProfit > 0 ? (
                      <>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Promoter Profit</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {`-${globalCurrency.formatted(calculated.showSummary.promoterProfit)}`}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {`-${formatCurrency(calculated.showSummary.promoterProfit)}`}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>Show Net, After Promoter Profit</td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {globalCurrency.formatted(calculated.showSummary.showNetAfterPromoterProfit)}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {formatCurrency(calculated.showSummary.showNetAfterPromoterProfit)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      </>
                    ) : null}
                    {budget.dollar_or_percentage_operator === 'AND' ? (
                      <>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>
                                    {budget.artist ? (
                                      <span>{budget.artist.name} Guarantee</span>
                                    ) : (
                                      <span>Artist Guarantee</span>
                                    )}
                                  </td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {globalCurrency.formatted(calculated.showSummary.artistGuarantee)}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {formatCurrency(calculated.showSummary.artistGuarantee)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        {calculated.showSummary.upsidePercentage > 0 ? toBeSharedRows : null}
                      </>
                    ) : (
                      <>
                        {calculated.showSummary.upsidePercentage > 0 ? toBeSharedRows : null}
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "3px 0px 3px 20px",
                            }}
                          >
                            <table style={{"width": "100%"}}>
                              {summaryTableColgroup(showCurrencyConversion)}
                              <tbody>
                                <tr>
                                  <td>
                                    {budget.artist ? (
                                      <span>{budget.artist.name} Guarantee</span>
                                    ) : (
                                      <span>Artist Guarantee</span>
                                    )}
                                  </td>
                                  {showCurrencyConversion ? (
                                    <td style={{"textAlign": "right"}}>
                                      {globalCurrency.formatted(calculated.showSummary.artistGuarantee)}
                                    </td>
                                  ) : null}
                                  <td style={{"textAlign": "right"}}>
                                    {formatCurrency(calculated.showSummary.artistGuarantee)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            colSpan={1}
                            style={{
                              width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                              textAlign: "center"
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      </>
                    )}
                    {calculated.showSummary.upsidePercentage > 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            padding: "3px 0px 3px 20px",
                          }}
                        >
                          <table style={{"width": "100%"}}>
                            {summaryTableColgroup(showCurrencyConversion)}
                            <tbody>
                              <tr>
                                <td>{artistPercentageDescription(budget, calculated)}</td>
                                {showCurrencyConversion ? (
                                  <td style={{"textAlign": "right"}}>
                                    {globalCurrency.formatted(calculated.showSummary.artistPercentage)}
                                  </td>
                                ) : null}
                                <td style={{"textAlign": "right"}}>
                                  {formatCurrency(calculated.showSummary.artistPercentage)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td
                          colSpan={1}
                          style={{
                            width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                            textAlign: "center"
                          }}
                        >
                          &nbsp;
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          height: "20px"
                        }}
                      >
                        &nbsp;
                      </td>
                      <td
                        colSpan={1}
                        style={{
                          width: getEditColumnWidth(isSettling, requestFormat, editedCompCount, editedKillCount, editedDropCount, budget),
                          textAlign: "center",
                          height: "20px"
                        }}
                      >
                        &nbsp;
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        ) : null}
        {isSettling && (requestFormat === "html" || filteredDeductions.length > 0) ? (
          <tr>
            <td colSpan={2}>
              <div style={{"marginTop": "16px"}}>
                <BudgetLineItemsTable items={filteredDeductions}
                                      title={"Deductions"}
                                      tableColgroup={tableColgroup(isSettling, requestFormat, showCurrencyConversion)}
                                      showCurrencyConversion={showCurrencyConversion}
                                      globalCurrency={globalCurrency}
                                      itemToEdit={itemToEdit}
                                      itemToEditChanged={itemToEditChanged}
                                      editedDropCountChanged={editedDropCountChanged}
                                      deleteSettlementOverride={deleteSettlementOverride}
                                      calculated={calculated}
                                      formatCurrency={formatCurrency}
                                      numberWithCommas={numberWithCommas}
                                      csrfToken={csrfToken}
                                      team={team}
                                      offer={offer}
                                      onClickAddSettlementLineItem={
                                        (e) => {
                                          e.preventDefault();

                                          createSettlementLineItem(csrfToken, team, {
                                            offer_id: offerId,
                                            section: "Deductions"
                                          });
                                        }
                                      }
                                      deleteSettlementLineItem={deleteSettlementLineItem}
                                      isSettling={isSettling}
                                      requestFormat={requestFormat}
                                      leftHandTitle={"RATE"}
                                      rightHandTitle={"QUANTITY"}
                                      totalTitle={"TOTAL"}
                                      totalRowLeftHand={"Total"}
                                      offerId={offerId}
                                      setBudgetState={setBudgetState}
                                      isV3={isV3}
                                      totalRowTotal={calculated.deductionsSection.total}>
                </BudgetLineItemsTable>
              </div>
            </td>
          </tr>
        ) : null}
        <tr>
          <td colSpan={2}>
            <div style={{"marginTop": "8px"}}>
              <table style={{"width": "100%", "borderSpacing": "0px 2px", "borderCollapse": "separate"}}>
                {tableColgroup(isSettling, requestFormat, currencyConversionIsEnabled)}
                <thead style={{"textAlign": "left"}}>
                  <tr style={{"background": "var(--od-c-f5f5f5, #F5F5F5)"}}>
                    <th style={{
                      "fontSize": "14px",
                      "color": "var(--od-c-000000, #000000)",
                      "padding": "6px 15px",
                      "textAlign": "left"
                    }}>
                      <strong>Artist Earnings</strong>
                    </th>
                    <th style={{
                      "fontWeight": "600",
                      "fontSize": "14px",
                      "color": "var(--od-c-999999, #999999)",
                      "textAlign": "right",
                      "padding": "6px 9px 6px 0px"
                    }}>
                      &nbsp;
                    </th>
                    <th style={{
                      "fontWeight": "600",
                      "fontSize": "14px",
                      "color": "var(--od-c-999999, #999999)",
                      "textAlign": "right",
                      "padding": "6px 9px 6px 0px"
                    }}>
                      &nbsp;
                    </th>
                    {currencyConversionIsEnabled ? (
                      <th style={{
                        "fontWeight": "600",
                        "fontSize": "14px",
                        "color": "var(--od-c-999999, #999999)",
                        "textAlign": "right",
                        "padding": "6px 9px 6px 0px"
                      }}>
                        {team.secondary_currency_symbol.toUpperCase()}
                      </th>
                    ) : null}
                    <th style={{
                      "fontWeight": "600",
                      "fontSize": "14px",
                      "color": "var(--od-c-999999, #999999)",
                      "textAlign": "right",
                      "padding": "6px 15px 6px 0px"
                    }}>
                      TOTAL
                    </th>
                    <th style={{
                      "padding": "6px 0px"
                    }}>
                      <span>&nbsp;</span>
                    </th>
                  </tr>
                </thead>
                <tbody style={{"fontSize": "14px"}}>
                  <tr>
                    <td style={{"padding": "3px 9px 3px 15px"}}>
                      Artist Guarantee and/or Percentage
                    </td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    {currencyConversionIsEnabled ? (
                      <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                        {globalCurrency.formatted(calculated.artistEarnings.artistGuaranteeAndOrPercentage)}
                      </td>
                    ) : null}
                    <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
                      <strong>
                        {formatCurrency(calculated.artistEarnings.artistGuaranteeAndOrPercentage)}
                      </strong>
                    </td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                  </tr>
                  {calculated.additionsSection.items.length > 0 ? (
                    <tr>
                      <td style={{"padding": "3px 9px 3px 15px"}}>
                        Total Additions
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      {currencyConversionIsEnabled ? (
                        <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                          {globalCurrency.formatted(calculated.artistEarnings.totalAdditions)}
                        </td>
                      ) : null}
                      <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
                        <strong>
                          {formatCurrency(calculated.artistEarnings.totalAdditions)}
                        </strong>
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    </tr>
                  ) : null}
                  {calculated.deductionsSection.items.length > 0 ? (
                    <tr>
                      <td style={{"padding": "3px 9px 3px 15px"}}>
                        Total Deductions
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      {currencyConversionIsEnabled ? (
                        <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                          {globalCurrency.formatted(calculated.artistEarnings.totalDeductions)}
                        </td>
                      ) : null}
                      <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
                        <strong>
                          {formatCurrency(calculated.artistEarnings.totalDeductions)}
                        </strong>
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    </tr>
                  ) : null}
                  {budget.has_tax_withholding ? (
                    <tr>
                      <td style={{"padding": "3px 9px 3px 15px"}}>
                        {`Tax Withholding (${budget.tax_withholding_percentage}% of taxable artist earnings)`}
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                      {currencyConversionIsEnabled ? (
                        <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                          {globalCurrency.formatted(calculated.artistEarnings.taxWithholding)}
                        </td>
                      ) : null}
                      <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
                        <strong>
                          {formatCurrency(calculated.artistEarnings.taxWithholding)}
                        </strong>
                      </td>
                      <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    </tr>
                  ) : null}
                  <tr style={{"fontSize": "16px"}}>
                    <td style={{"padding": "3px 9px 3px 15px"}}>
                      <strong>
                        <span>
                          {`${isSettling ? "Total Due" : "Total Potential Due"} ${budget.artist ? budget.artist.name : ""}`}
                        </span>
                      </strong>
                    </td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                    {currencyConversionIsEnabled ? (
                      <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                        {globalCurrency.formatted(calculated.artistEarnings.artistSettlementDue)}
                      </td>
                    ) : null}
                    <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
                      <strong>
                        {formatCurrency(calculated.artistEarnings.artistSettlementDue)}
                      </strong>
                    </td>
                    <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>
                  </tr>
                  <ArtistPaymentRows items={calculated.artistPaymentsSection.items}
                                     remainingDue={calculated.artistEarnings.artistDueAfterPayments}
                                     artistName={budget.artist ? budget.artist.name : ""}
                                     isSettling={isSettling}
                                     requestFormat={requestFormat}
                                     showCurrencyConversion={!!currencyConversionIsEnabled}
                                     globalCurrency={globalCurrency}
                                     formatCurrency={formatCurrency}
                                     numberWithCommas={numberWithCommas}
                                     csrfToken={csrfToken}
                                     team={team}
                                     offer={offer}
                                     offerId={offerId}
                                     isV3={isV3}
                                     itemToEdit={itemToEdit}
                                     itemToEditChanged={itemToEditChanged}
                                     editedDropCountChanged={editedDropCountChanged}
                                     createSettlementLineItem={createSettlementLineItem}
                                     deleteSettlementLineItem={deleteSettlementLineItem}
                                     deleteSettlementOverride={deleteSettlementOverride}
                                     setBudgetState={setBudgetState} />
                </tbody>
              </table>
            </div>
          </td>
        </tr>
        <SettlementVenueNetTable
          offer={offer}
          showSummary={calculated.showSummary}
          isSettling={isSettling}
          requestFormat={requestFormat}
          colgroup={tableColgroup(isSettling, requestFormat, currencyConversionIsEnabled)}
          formatCurrency={formatCurrency}
          formatConvertedCurrency={(amount) => globalCurrency.formatted(amount)}
          currencyConversionIsEnabled={!!currencyConversionIsEnabled}
          showCurrencyConversion={!!showCurrencyConversion}
          secondaryCurrencySymbol={team.secondary_currency_symbol}
          isSavingShowOnPdf={isSavingAttribute('display_venue_net_on_settlement_pdf')}
          onShowOnPdfChange={(checked) => autoSaveOffer({ display_venue_net_on_settlement_pdf: checked })}
        />
      </tbody>
    </table>
  </div>
)};

SettlementTable.propTypes = {
  budget: PropTypes.object.isRequired,
  setBudgetState: PropTypes.func.isRequired,
  calculated: PropTypes.object,
  offer: PropTypes.object.isRequired,
  confirm: PropTypes.object,
  createSettlementLineItem: PropTypes.func.isRequired,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  offerId: PropTypes.string.isRequired,
  deleteSettlementLineItem: PropTypes.func.isRequired,
  updateSettlementLineItemTaxWithholdingExemption: PropTypes.func,
  itemToEdit: PropTypes.object,
  itemToEditChanged: PropTypes.func.isRequired,
  deleteSettlementOverride: PropTypes.func.isRequired,
  requestFormat: PropTypes.string,
  isSettling: PropTypes.bool,
  isV3: PropTypes.bool,
  editedDropCount: PropTypes.string,
  editedDropCountChanged: PropTypes.func.isRequired,
  editedCompCount: PropTypes.string,
  editedCompCountChanged: PropTypes.func.isRequired,
  editedKillCount: PropTypes.string,
  editedKillCountChanged: PropTypes.func.isRequired,
  saveOverrides: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  offerChanged: PropTypes.func.isRequired,
  isSavingChanged: PropTypes.func.isRequired,
  getActiveTags: PropTypes.func.isRequired,
  getAvailableTags: PropTypes.func.isRequired,
};

export default SettlementTable;
